import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { FiAlertTriangle } from 'react-icons/fi';
import './DangerConfirmModal.css';

/**
 * The confirmation for something that destroys data and cannot be undone.
 *
 * The app already had this pattern in two places — `DeleteAccountModal` and
 * `ShutDownSocietyModal` — and they are the strongest thing in the codebase:
 * type the word before the button will do anything. Everything ELSE severe was
 * a plain "are you sure" or, worse, a native `window.confirm()`. Deleting a
 * competition day removes every player's rounds, hole scores and bets for that
 * day, permanently, with no soft-delete and no undo — the same order of
 * consequence — but took one more click on a dialog indistinguishable from the
 * one that deletes a single game.
 *
 * So this is that pattern, extracted, and it does the two things a reflexive
 * "OK" cannot survive:
 *
 *   · it LISTS WHAT WILL BE LOST in figures (`items`), from the server's own
 *     count rather than from prose. "3 rounds · 48 cards · 812 hole scores"
 *     tells an organiser whether this is the empty duplicate they meant to
 *     delete; "all match history" does not.
 *   · it requires the word typed, so the destructive button cannot be reached
 *     by the muscle memory that dismissed the last four dialogs.
 *
 * Reach for it only where the consequence genuinely warrants it. A delete the
 * server refuses when there is anything to lose (players, courses) is already
 * capped, and a plain `ConfirmationModal` is the proportionate answer there —
 * over-using this trains people to type DELETE without reading, which is the
 * failure mode it exists to prevent.
 */
const DangerConfirmModal = ({
  isOpen,
  title,
  subject,
  body,
  items = [],
  confirmWord = 'DELETE',
  confirmLabel = 'Delete',
  busyLabel = 'Deleting…',
  errorMessage,
  isSubmitting = false,
  onConfirm,
  onCancel,
}) => {
  const [typed, setTyped] = useState('');

  // Reopening must not inherit the last visit's typed word — otherwise the
  // second delete of a session is a single click, which is the whole thing
  // this modal exists to prevent.
  useEffect(() => { if (!isOpen) setTyped(''); }, [isOpen]);

  if (!isOpen) return null;

  const armed = typed.trim().toUpperCase() === confirmWord && !isSubmitting;

  return ReactDOM.createPortal(
    <div className="dcm-overlay" onClick={isSubmitting ? undefined : onCancel}>
      <div
        className="dcm-sheet"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="dcm-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dcm-icon-wrap"><FiAlertTriangle size={28} className="dcm-icon" /></div>

        <h2 className="dcm-title" id="dcm-title">{title}</h2>
        {subject && <p className="dcm-subject">{subject}</p>}

        {items.length > 0 && (
          <div className="dcm-losses">
            <p className="dcm-losses-head">This permanently deletes</p>
            <ul className="dcm-losses-list">
              {items.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
        )}

        {body && <p className="dcm-body">{body}</p>}
        <p className="dcm-body dcm-body--final">This cannot be undone.</p>

        {errorMessage && <div className="dcm-error">{errorMessage}</div>}

        <div className="dcm-confirm-section">
          <label className="dcm-confirm-label" htmlFor="dcm-confirm-input">
            Type <strong>{confirmWord}</strong> to confirm
          </label>
          <input
            id="dcm-confirm-input"
            className="dcm-confirm-input"
            type="text"
            value={typed}
            onChange={(e) => setTyped(e.target.value.toUpperCase())}
            placeholder={confirmWord}
            disabled={isSubmitting}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck="false"
          />
        </div>

        <div className="dcm-actions">
          <button className="dcm-btn dcm-btn--cancel" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </button>
          <button className="dcm-btn dcm-btn--confirm" onClick={onConfirm} disabled={!armed}>
            {isSubmitting ? busyLabel : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DangerConfirmModal;
